import { useEffect, useMemo } from 'react';
import {
  Card,
  FullWidthLayout,
  Input,
  StyledHorizontalLayout,
} from '@versita/frontend/campus-ui-web';
import { useUserWidget } from '../../hooks/useUserWidget';
import {
  ListCourseInstancesApiResponse,
  ListCoursesApiResponse,
  useListCourseInstancesQuery,
  useListCoursesQuery,
} from '../../services/generated/studentPortalApi';
import { AdminTableFilters } from '../../organisms/AdminTableFilters';
import { AdminTableStudent } from '../../organisms/AdminTableStudent';
import { AdminTableActions } from '../../molecules/AdminTableActions';
import { useHandleQueryError } from '@versita/frontend/api-utils';
import { useTimetableManagerState, useAppDispatch } from '../../state';
import {
  setFilteredItems,
  setSelectedIds,
  setTextFilter,
} from '../../state/slices/timetableManagerSlice';
import { and, anyPass, filter, pipe, propEq, props, when } from 'ramda';
import {
  Course,
  CourseInstance,
  PublishableCourselike,
} from '@versita/shared/models';
import { AdminTableCourse } from '../../organisms/AdminTableCourse';
import { AdminNav } from '../../molecules/AdminNav';

interface TimetableAdminProps {
  pageTitle: string;
  flavour: 'STUDENT' | 'COURSE';
}

const useItemsQuery = (flavour: 'STUDENT' | 'COURSE') =>
  flavour === 'COURSE' ? useListCoursesQuery : useListCourseInstancesQuery;

/**
 *
 */
export const TimeTableAdmin = ({ pageTitle, flavour }: TimetableAdminProps) => {
  const userWidget = useUserWidget();
  const dispatch = useAppDispatch();
  const { filteredItems, selectedIds, textFilter, filters, academicYear } =
    useTimetableManagerState();

  const { data: items, isLoading } = useHandleQueryError(
    useItemsQuery(flavour)(
      { academicYear },
      {
        pollingInterval: 10000,
        refetchOnMountOrArgChange: true,
      },
    ),
  );

  useEffect(() => {
    // Filter selectedIds based on filteredItems
    const validSelectedIds = selectedIds.filter((id) =>
      filteredItems.some((item) => item.id === id),
    );

    // Update selectedIds only if there's a change
    if (validSelectedIds.length !== selectedIds.length) {
      dispatch(setSelectedIds(validSelectedIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, filteredItems]);

  const isCourseInstancesResponse = (
    response: ListCourseInstancesApiResponse | ListCoursesApiResponse,
  ): response is ListCourseInstancesApiResponse => 'data' in response;
  const viewItems: PublishableCourselike[] = useMemo(
    () =>
      items && isCourseInstancesResponse(items) ? items?.data : items || [],
    [items],
  );
  useEffect(() => {
    const filterFunctions = {
      published: propEq(true, 'published'),
      unpublished: (v: PublishableCourselike) =>
        and(propEq(false, 'published', v), propEq(false, 'willNotPublish', v)),
      willNotPublish: propEq(true, 'willNotPublish'),
    };
    const activeFilters = props(filters, filterFunctions);
    const isCourseInstance = (
      item: PublishableCourselike,
    ): item is CourseInstance => 'courseId' in item;
    const filterItems = pipe(
      when(
        () => !!textFilter,
        filter((item: PublishableCourselike) =>
          isCourseInstance(item)
            ? item.courseId.toLowerCase().includes(textFilter.toLowerCase()) ||
            item.courseName.toLowerCase().includes(textFilter.toLowerCase())
            : item.id.toLowerCase().includes(textFilter.toLowerCase()) ||
            (item as Course).name
              .toLowerCase()
              .includes(textFilter.toLowerCase()),
        ),
      ),
      when(
        () => filters.length > 0,
        filter<PublishableCourselike>(anyPass(activeFilters)),
      ),
    );
    const filtered = filterItems(viewItems);
    dispatch(setFilteredItems(filtered));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textFilter, filters, viewItems]);

  return (
    <FullWidthLayout
      userWidget={userWidget}
      heading={{
        title: 'Timetable Admin',
        subtitle: pageTitle,
      }}
    >
      <AdminNav />
      <Card $fullWidth={true} onClick={(e) => e.stopPropagation()}>
        <StyledHorizontalLayout>
          <Input
            style={{ maxWidth: '32rem' }}
            label="Course Filter"
            description="Filter by course name (or code if you know it)"
            id="course-filter"
            onChange={(event) =>
              dispatch(
                setTextFilter(event.currentTarget.value.replace(/\//g, '.')),
              )
            }
            role="search"
          />
          <AdminTableFilters flavour={flavour} />
          {selectedIds.length > 0 && <AdminTableActions flavour={flavour} />}
        </StyledHorizontalLayout>
      </Card>
      {flavour === 'STUDENT' ? (
        <AdminTableStudent isLoading={isLoading} />
      ) : (
        <AdminTableCourse isLoading={isLoading} />
      )}
    </FullWidthLayout>
  );
};

export default TimeTableAdmin;
